import React from 'react';
import { motion } from 'framer-motion';
import type { CSS } from '@components-shared/src/stitches';
import { styled } from '@components-shared/src/stitches';

export type IconButtonProps = {
  testId?: string;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'inverted';
  size?: number;
  iconSize?: number;
  icon: React.ReactNode;
  animate?: boolean;
  css?: CSS;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { testId, icon, css, size = 32, iconSize = 20, variant = 'primary', animate = false, ...props },
  forwardedRef,
) {
  const iconButton = (
    <StyledIconButton
      data-testid={testId}
      variant={variant}
      aria-label="icon-button"
      aria-disabled={props?.disabled}
      ref={forwardedRef}
      css={{
        ...css,
        size: size,
        svg: {
          size: iconSize,
        },
      }}
      {...props}
    >
      {icon}
    </StyledIconButton>
  );
  return !animate ? (
    iconButton
  ) : (
    <motion.div
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.7 }}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 20,
      }}
    >
      {iconButton}
    </motion.div>
  );
});

const StyledIconButton = styled('button', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  size: 32,
  borderRadius: '50%',
  color: '$base500',
  backgroundColor: 'transparent',
  transitionDuration: '0.3s',
  svg: {
    size: 20,
  },
  '&:hover': {
    color: '$black',
  },

  '&:focus': {
    color: '$base500',
    backgroundColor: 'transparent',
  },

  '&:active': {
    color: '$black',
  },
  '&:disabled': {
    color: '$base300',
    backgroundColor: 'transparent',
    cursor: 'not-allowed',
  },

  variants: {
    variant: {
      primary: {
        // hover, focus, and active states are defined in order to
        // make the styled and interaction with the button more effective
        '&:hover': {
          backgroundColor: '$base100',
        },

        '&:focus': {
          backgroundColor: 'transparent',
        },

        '&:active': {
          backgroundColor: '$base100',
        },

        '&:disabled': {
          backgroundColor: 'transparent',
        },
      },
      secondary: {
        size: 42,
        lift: 'tertiary',
        backgroundColor: '$white',

        '&:hover': {
          backgroundColor: '$white',
          lift: 'primary',
          color: '$black',
        },

        '&:disabled': {
          lift: 'tertiary',
          color: '$base500',
          opacity: 0.3,
        },
      },

      tertiary: {
        '&:hover, &:focus, &:active': {
          backgroundColor: 'transparent',
        },

        '&:disabled': {
          backgroundColor: 'transparent',
        },
      },
      inverted: {
        color: '$white',

        '&:hover': {
          color: '$white',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },

        '&:focus': {
          color: '$white',
          backgroundColor: 'transparent',
        },

        '&:active': {
          color: '$white',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },

        '&:disabled': {
          color: 'rgba(255, 255, 255, 0.3)',
          backgroundColor: 'transparent',
        },
      },
    },
  },
});
